import { without, zip } from "lodash";
import React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { useAuthentication } from "../../../../context/authentication";
import { useLocations } from "../../../../context/locations";
import { useTransmitScales } from "../../../../context/transmitScales";
import classNames from "../../../../utils/classNames";
import { ClassificationValue } from "../../../../utils/hotspotUtils";
import { kRingMemoized } from "../../../../utils/memoizedMethods";

const NeighborHexesTab: React.FC<RouteComponentProps<{ h3Index: string }>> = (
  props
) => {
  const { wrapActionWithAuth } = useAuthentication();
  const { setSimulatedLocations } = useLocations();
  const { getHexTransmitScaleInfo, generateLocationsToReachTargetDensity } =
    useTransmitScales();

  const { limitCanBeIncreased } = getHexTransmitScaleInfo(
    props.match.params.h3Index
  );

  return (
    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3 xs:grid-cols-2">
      {getHexNeighborsWithLabel(props.match.params.h3Index).map(
        ([h3Index, letter]) => {
          const { shortOfDensityTarget, densityStatusMessage, transmitScale } =
            getHexTransmitScaleInfo(h3Index);
          return (
            <div key={h3Index}>
              <div
                className={classNames(
                  limitCanBeIncreased && shortOfDensityTarget > 0
                    ? "border-2 border-green-300 hover:border-green-400 focus-within:ring-green-500"
                    : "border border-gray-300 hover:border-gray-400 focus-within:ring-hotspotty-400",
                  "block relative rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-offset-2 group"
                )}
              >
                <Link to={`/hexes/${h3Index}/transmit-scale/hotspots-in-hex`}>
                  <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 px-6 py-5 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">Neighbor {letter}</p>
                      <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                        {densityStatusMessage}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <ClassificationValue
                        value={transmitScale}
                        hexProps={{
                          width: 20,
                          height: 22.8
                        }}
                      />
                    </div>
                  </div>
                </Link>

                {limitCanBeIncreased && shortOfDensityTarget > 0 && (
                  <div
                    className="bg-green-400 hover:bg-green-500 cursor-pointer"
                    onClick={wrapActionWithAuth(() => {
                      setSimulatedLocations((prevLocations) =>
                        prevLocations.concat(
                          generateLocationsToReachTargetDensity(
                            h3Index,
                            (locationNumber) =>
                              `Reach density target, location ${locationNumber}`
                          )
                        )
                      );
                    })}
                  >
                    <div className="max-w-7xl mx-auto py-3">
                      <div className="flex items-center justify-center">
                        <p className="font-medium text-white">
                          Add {shortOfDensityTarget} to reach density target
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export default NeighborHexesTab;

//
// Utils
//

export const getHexNeighborsWithLabel = (h3Index: string) => {
  const letters = ["A", "B", "C", "D", "E", "F"];
  const neighborHexes = without(kRingMemoized(h3Index, 1), h3Index);
  return zip(neighborHexes, letters) as [string, string][]; // Response: [neighborHex, neighborLetter][]
};
